import React, { useState } from 'react';

// ============================================================================
// CONTRACTOR PORTAL - Standalone Application
// Accessed via magic link sent to contractors
// ============================================================================

// Compress and resize image to fit within D1 storage limits
const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const compressedUrl = canvas.toDataURL('image/jpeg', quality);
        resolve({
          id: Date.now() + Math.random(),
          name: file.name,
          size: compressedUrl.length,
          type: 'image/jpeg',
          url: compressedUrl
        });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

// ============================================================================
// STYLES
// ============================================================================

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&family=Source+Sans+3:wght@300;400;500;600;700&display=swap');

  :root {
    --bg-primary: #0a0a0b;
    --bg-secondary: #111113;
    --bg-tertiary: #1a1a1d;
    --bg-elevated: #222225;
    --border-color: #2a2a2d;
    --border-focus: #3d3d42;
    --text-primary: #f5f5f7;
    --text-secondary: #a1a1a6;
    --text-muted: #6e6e73;
    --accent-blue: #0a84ff;
    --accent-green: #30d158;
    --accent-orange: #ff9f0a;
    --accent-red: #ff453a;
    --accent-purple: #bf5af2;
    --shadow-sm: 0 1px 2px rgba(0,0,0,0.4);
    --shadow-md: 0 4px 12px rgba(0,0,0,0.5);
    --shadow-lg: 0 8px 32px rgba(0,0,0,0.6);
    --radius-sm: 6px;
    --radius-md: 10px;
    --radius-lg: 16px;
    --font-mono: 'JetBrains Mono', monospace;
    --font-sans: 'Source Sans 3', -apple-system, sans-serif;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: var(--font-sans);
    background: var(--bg-primary);
    color: var(--text-primary);
    line-height: 1.6;
    min-height: 100vh;
  }

  /* Login Screen */
  .login-container {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: 
      radial-gradient(ellipse at 20% 80%, rgba(48, 209, 88, 0.08) 0%, transparent 50%),
      radial-gradient(ellipse at 80% 20%, rgba(10, 132, 255, 0.06) 0%, transparent 50%),
      var(--bg-primary);
    padding: 20px;
  }

  .login-card {
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-lg);
    padding: 48px;
    width: 100%;
    max-width: 420px;
    box-shadow: var(--shadow-lg);
  }

  .login-logo {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 32px;
  }

  .login-logo-icon {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, var(--accent-green), var(--accent-blue));
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .login-logo-text {
    font-family: var(--font-mono);
    font-size: 20px;
    font-weight: 600;
    letter-spacing: -0.5px;
  }

  .login-title {
    font-size: 28px;
    font-weight: 600;
    margin-bottom: 8px;
    letter-spacing: -0.5px;
  }

  .login-subtitle {
    color: var(--text-secondary);
    margin-bottom: 32px;
    font-size: 15px;
  }

  .form-group {
    margin-bottom: 20px;
  }

  .form-label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary);
    margin-bottom: 8px;
  }

  .form-input,
  .form-textarea,
  .form-select {
    width: 100%;
    padding: 12px 16px;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: 14px;
    font-family: inherit;
    transition: all 0.15s ease;
  }

  .form-input:focus,
  .form-textarea:focus,
  .form-select:focus {
    outline: none;
    border-color: var(--accent-blue);
    box-shadow: 0 0 0 3px rgba(10, 132, 255, 0.1);
  }

  .form-textarea {
    min-height: 120px;
    resize: vertical;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .primary-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 14px 24px;
    background: var(--accent-green);
    border: none;
    border-radius: var(--radius-md);
    color: white;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .primary-btn:hover {
    background: #28b84d;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(48, 209, 88, 0.3);
  }

  /* Portal Layout */
  .portal-layout {
    min-height: 100vh;
    background: var(--bg-primary);
  }

  .portal-header {
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-color);
    padding: 16px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 0;
    z-index: 100;
  }

  .portal-logo {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .portal-logo-icon {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, var(--accent-green), var(--accent-blue));
    border-radius: var(--radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .portal-logo-text {
    font-family: var(--font-mono);
    font-size: 16px;
    font-weight: 600;
  }

  .portal-logo-subtitle {
    font-size: 12px;
    color: var(--text-muted);
  }

  .portal-user {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .portal-user-info {
    text-align: right;
  }

  .portal-user-name {
    font-size: 14px;
    font-weight: 500;
  }

  .portal-user-email {
    font-size: 12px;
    color: var(--text-muted);
  }

  .logout-btn {
    background: none;
    border: 1px solid var(--border-color);
    border-radius: var(--radius-sm);
    color: var(--text-secondary);
    padding: 8px 12px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .logout-btn:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-focus);
    color: var(--text-primary);
  }

  .portal-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 32px;
  }

  .page-header {
    margin-bottom: 32px;
  }

  .page-title {
    font-size: 28px;
    font-weight: 600;
    letter-spacing: -0.5px;
    margin-bottom: 4px;
  }

  .page-subtitle {
    color: var(--text-secondary);
    font-size: 15px;
  }

  /* Tabs */
  .portal-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 24px;
    border-bottom: 1px solid var(--border-color);
    padding-bottom: 12px;
    flex-wrap: wrap;
  }

  .portal-tab {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: transparent;
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    color: var(--text-secondary);
    font-size: 14px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .portal-tab:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-focus);
    color: var(--text-primary);
  }

  .portal-tab.active {
    background: rgba(48, 209, 88, 0.15);
    border-color: var(--accent-green);
    color: var(--accent-green);
  }

  .tab-badge {
    background: var(--accent-red);
    color: white;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 10px;
  }

  .tab-badge.yellow {
    background: var(--accent-orange);
  }

  .tab-badge.green {
    background: var(--accent-green);
  }

  /* Defect Cards */
  .defects-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
  }

  .defect-card {
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    padding: 20px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .defect-card:hover {
    border-color: var(--accent-green);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  .defect-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .defect-id {
    font-family: var(--font-mono);
    font-size: 13px;
    font-weight: 500;
    color: var(--accent-blue);
  }

  .severity-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
  }

  .severity-badge.high {
    background: rgba(255, 69, 58, 0.15);
    color: var(--accent-red);
  }

  .severity-badge.medium {
    background: rgba(255, 159, 10, 0.15);
    color: var(--accent-orange);
  }

  .severity-badge.low {
    background: rgba(48, 209, 88, 0.15);
    color: var(--accent-green);
  }

  .severity-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }

  .defect-card-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 4px;
    line-height: 1.4;
  }

  .defect-card-store {
    font-size: 13px;
    color: var(--accent-green);
    margin-bottom: 8px;
  }

  .defect-card-description {
    font-size: 14px;
    color: var(--text-secondary);
    margin-bottom: 16px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .defect-card-quote {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: var(--bg-tertiary);
    border-radius: var(--radius-sm);
    margin-bottom: 16px;
  }

  .quote-label {
    font-size: 12px;
    color: var(--text-muted);
  }

  .quote-value {
    font-family: var(--font-mono);
    font-size: 16px;
    font-weight: 600;
    color: var(--accent-green);
  }

  .defect-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 16px;
    border-top: 1px solid var(--border-color);
  }

  .defect-card-date {
    font-size: 12px;
    color: var(--text-muted);
  }

  .action-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: transparent;
    border: 1px solid var(--accent-green);
    border-radius: var(--radius-sm);
    color: var(--accent-green);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .action-btn:hover {
    background: var(--accent-green);
    color: white;
  }

  /* Empty State */
  .empty-state {
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    text-align: center;
    padding: 60px 20px;
    color: var(--text-secondary);
  }

  .empty-state-icon {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  .empty-state-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 8px;
  }

  .empty-state-text {
    font-size: 14px;
  }

  /* Modal */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
    animation: fadeIn 0.2s ease;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .modal {
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-lg);
    width: 100%;
    max-width: 640px;
    max-height: 90vh;
    overflow-y: auto;
    animation: slideUp 0.3s ease;
  }

  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(20px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    border-bottom: 1px solid var(--border-color);
  }

  .modal-close {
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: 4px;
    transition: color 0.15s;
  }

  .modal-close:hover {
    color: var(--text-primary);
  }

  .modal-body {
    padding: 24px;
  }

  .detail-section {
    margin-bottom: 24px;
  }

  .detail-section-title {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
    margin-bottom: 12px;
  }

  .detail-description {
    background: var(--bg-tertiary);
    padding: 16px;
    border-radius: var(--radius-sm);
    font-size: 14px;
    line-height: 1.6;
    color: var(--text-secondary);
  }

  .detail-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .detail-item {
    background: var(--bg-tertiary);
    padding: 12px 16px;
    border-radius: var(--radius-sm);
  }

  .detail-item-label {
    font-size: 12px;
    color: var(--text-muted);
    margin-bottom: 4px;
  }

  .detail-item-value {
    font-size: 14px;
    font-weight: 500;
  }

  .detail-images-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
  }

  .detail-image-item {
    border-radius: var(--radius-sm);
    overflow: hidden;
    border: 1px solid var(--border-color);
  }

  .detail-image-item img {
    width: 100%;
    height: 120px;
    object-fit: cover;
    display: block;
  }

  /* Quote Card */
  .quote-card {
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    padding: 20px;
  }

  .quote-amount-large {
    font-size: 28px;
    font-weight: 700;
    font-family: var(--font-mono);
    color: var(--accent-green);
    margin-bottom: 8px;
  }

  .quote-description-text {
    font-size: 14px;
    color: var(--text-secondary);
    margin-bottom: 12px;
  }

  .quote-meta {
    font-size: 12px;
    color: var(--text-muted);
  }

  .quote-status-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
    padding: 12px;
    background: rgba(255, 159, 10, 0.1);
    border: 1px solid rgba(255, 159, 10, 0.3);
    border-radius: var(--radius-sm);
    color: var(--accent-orange);
    font-size: 14px;
    font-weight: 500;
  }

  .quote-status-banner.approved {
    background: rgba(48, 209, 88, 0.1);
    border-color: rgba(48, 209, 88, 0.3);
    color: var(--accent-green);
  }

  .status-icon {
    font-size: 16px;
  }

  /* Image Upload */
  .image-upload-zone {
    border: 2px dashed var(--border-color);
    border-radius: var(--radius-md);
    padding: 32px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    background: var(--bg-tertiary);
  }

  .image-upload-zone:hover {
    border-color: var(--accent-green);
    background: rgba(48, 209, 88, 0.05);
  }

  .image-upload-zone.drag-active {
    border-color: var(--accent-green);
    background: rgba(48, 209, 88, 0.1);
    border-style: solid;
  }

  .upload-icon {
    color: var(--text-muted);
    margin-bottom: 12px;
    display: flex;
    justify-content: center;
  }

  .upload-text {
    font-size: 14px;
    color: var(--text-secondary);
    margin-bottom: 4px;
  }

  .upload-text-primary {
    color: var(--accent-green);
    font-weight: 500;
  }

  .upload-hint {
    font-size: 12px;
    color: var(--text-muted);
  }

  .image-preview-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
    margin-top: 16px;
  }

  .image-preview-item {
    position: relative;
    border-radius: var(--radius-sm);
    overflow: hidden;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
  }

  .image-preview-img {
    width: 100%;
    height: 100px;
    object-fit: cover;
    display: block;
  }

  .image-preview-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .image-preview-item:hover .image-preview-overlay {
    opacity: 1;
  }

  .image-remove-btn {
    background: var(--accent-red);
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    cursor: pointer;
  }

  .image-preview-info {
    padding: 8px;
  }

  .image-preview-name {
    display: block;
    font-size: 11px;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .image-preview-size {
    font-size: 10px;
    color: var(--text-muted);
  }

  /* Submit Button */
  .submit-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 24px;
    background: var(--accent-green);
    border: none;
    border-radius: var(--radius-md);
    color: white;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 200px;
  }

  .submit-btn:hover {
    background: #28b84d;
    transform: translateY(-1px);
  }

  /* Toast */
  .toast {
    position: fixed;
    bottom: 24px;
    right: 24px;
    background: var(--bg-elevated);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    padding: 16px 20px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-shadow: var(--shadow-lg);
    animation: slideIn 0.3s ease;
    z-index: 1100;
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .toast-icon {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    background: rgba(48, 209, 88, 0.15);
    color: var(--accent-green);
  }

  .toast-message {
    font-size: 14px;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .portal-header {
      padding: 12px 16px;
      flex-direction: column;
      gap: 12px;
    }

    .portal-content {
      padding: 16px;
    }

    .defects-grid {
      grid-template-columns: 1fr;
    }

    .form-row {
      grid-template-columns: 1fr;
    }

    .detail-grid {
      grid-template-columns: 1fr;
    }
  }
`;

// ============================================================================
// ICONS
// ============================================================================

const Icons = {
  Wrench: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
    </svg>
  ),
  Close: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Check: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  Camera: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  ),
  Arrow: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="5" y1="12" x2="19" y2="12"/>
      <polyline points="12 5 19 12 12 19"/>
    </svg>
  )
};

// ============================================================================
// COMPONENTS
// ============================================================================

// Contractor Login
const ContractorLogin = ({ onLogin, error }) => {
  const [email, setEmail] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [emailSent, setEmailSent] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setIsSubmitting(true);
    await onLogin(email.trim());
    setIsSubmitting(false);
    setEmailSent(true);
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo">
          <div className="login-logo-icon">
            <Icons.Wrench />
          </div>
          <span className="login-logo-text">Contractor Portal</span>
        </div>
        <h1 className="login-title">Welcome</h1>
        <p className="login-subtitle">Access your assigned defects, submit quotes, and complete jobs.</p>
        
        {error && (
          <div style={{ 
            padding: 12, 
            background: 'rgba(255, 69, 58, 0.1)', 
            border: '1px solid rgba(255, 69, 58, 0.3)',
            borderRadius: 'var(--radius-md)', 
            marginBottom: 20,
            fontSize: 14,
            color: 'var(--accent-red)'
          }}>
            {error}
          </div>
        )}
        
        {emailSent ? (
          <div style={{ 
            padding: 20, 
            background: 'rgba(48, 209, 88, 0.1)', 
            border: '1px solid rgba(48, 209, 88, 0.3)',
            borderRadius: 'var(--radius-md)', 
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✉️</div>
            <div style={{ fontWeight: 500, marginBottom: 8, color: 'var(--accent-green)' }}>Check your email</div>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
              If an account exists for <strong>{email}</strong>, we've sent a login link. The link is valid for 7 days.
            </p>
            <button 
              type="button" 
              onClick={() => { setEmailSent(false); setEmail(''); }}
              style={{
                marginTop: 16,
                padding: '8px 16px',
                background: 'transparent',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: 13
              }}
            >
              Try a different email
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                type="email"
                className="form-input"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
            
            <button type="submit" className="primary-btn" disabled={isSubmitting || !email.trim()}>
              {isSubmitting ? 'Sending...' : 'Send Login Link'}
            </button>
          </form>
        )}
        
        <div style={{ marginTop: 24, padding: 16, background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', fontSize: 13, color: 'var(--text-muted)' }}>
          <strong style={{ color: 'var(--text-secondary)' }}>How it works:</strong> Enter your email to receive a secure login link. No password needed!
        </div>
      </div>
    </div>
  );
};

// Defect Detail Modal
const DefectModal = ({ defect, activeTab, onClose, onSubmitQuote, onCompleteJob }) => {
  const [quoteAmount, setQuoteAmount] = useState('');
  const [quoteDescription, setQuoteDescription] = useState('');
  const [estimatedCompletionDate, setEstimatedCompletionDate] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');
  const [completionImages, setCompletionImages] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleSubmitQuote = (e) => {
    e.preventDefault();
    onSubmitQuote(defect.id, {
      amount: parseFloat(quoteAmount),
      description: quoteDescription,
      estimatedCompletionDate: estimatedCompletionDate || null,
      submitted: new Date().toISOString().split('T')[0]
    });
    onClose();
  };

  const handleCompleteJob = (e) => {
    e.preventDefault();
    onCompleteJob(defect.id, {
      notes: completionNotes,
      images: completionImages,
      completedDate: new Date().toISOString().split('T')[0]
    });
    onClose();
  };

  const handleFiles = async (files) => {
    const validFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    for (const file of validFiles) {
      const compressed = await compressImage(file);
      setCompletionImages(prev => [...prev, compressed]);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const removeImage = (imageId) => {
    setCompletionImages(prev => prev.filter(img => img.id !== imageId));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <span className="defect-id" style={{ marginRight: 12 }}>{defect.id}</span>
            <span className={`severity-badge ${defect.severity}`}>
              <span className="severity-dot"></span>
              {defect.severity.charAt(0).toUpperCase() + defect.severity.slice(1)}
            </span>
          </div>
          <button className="modal-close" onClick={onClose}>
            <Icons.Close />
          </button>
        </div>
        <div className="modal-body">
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>{defect.title}</h2>
          <p style={{ color: 'var(--accent-green)', fontSize: 14, marginBottom: 24 }}>Store: {defect.location}</p>
          
          <div className="detail-section">
            <div className="detail-section-title">Description</div>
            <div className="detail-description">{defect.description}</div>
          </div>

          {defect.images && defect.images.length > 0 && (
            <div className="detail-section">
              <div className="detail-section-title">Defect Images</div>
              <div className="detail-images-grid">
                {defect.images.map((image, index) => (
                  <div key={image.id || index} className="detail-image-item">
                    <img src={image.url} alt={image.name || `Image ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="detail-section">
            <div className="detail-section-title">Details</div>
            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-item-label">Reported By</div>
                <div className="detail-item-value">{defect.reportedBy}</div>
              </div>
              <div className="detail-item">
                <div className="detail-item-label">Report Date</div>
                <div className="detail-item-value">{defect.reportedDate}</div>
              </div>
            </div>
          </div>

          {/* Revision Comments Alert */}
          {defect.status === 'revision_requested' && defect.revisionComments && (
            <div style={{
              background: 'rgba(255, 159, 10, 0.1)',
              border: '1px solid rgba(255, 159, 10, 0.3)',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <div style={{ fontWeight: 600, color: '#ff9f0a', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                ⚠️ Revision Requested
              </div>
              <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.5' }}>
                {defect.revisionComments}
              </p>
              {defect.revisionRequestedDate && (
                <p style={{ margin: '8px 0 0 0', color: 'var(--text-muted)', fontSize: '12px' }}>
                  Requested on {defect.revisionRequestedDate}
                </p>
              )}
            </div>
          )}

          {/* Submit Quote Form */}
          {activeTab === 'pending_quote' && (
            <form onSubmit={handleSubmitQuote}>
              <div className="detail-section">
                <div className="detail-section-title">
                  {defect.status === 'revision_requested' ? 'Submit Revised Quote' : 'Submit Your Quote'}
                </div>
                <div className="form-group">
                  <label className="form-label">Quote Amount (£) *</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    value={quoteAmount}
                    onChange={(e) => setQuoteAmount(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Estimated Completion Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={estimatedCompletionDate}
                    onChange={(e) => setEstimatedCompletionDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Description of Work *</label>
                  <textarea
                    className="form-textarea"
                    placeholder="Describe the work to be carried out, materials needed, etc."
                    value={quoteDescription}
                    onChange={(e) => setQuoteDescription(e.target.value)}
                    required
                  />
                </div>
              </div>
              <button type="submit" className="submit-btn">
                Submit Quote
              </button>
            </form>
          )}

          {/* Awaiting Approval View */}
          {activeTab === 'awaiting_approval' && defect.quote && (
            <div className="detail-section">
              <div className="detail-section-title">Your Submitted Quote</div>
              <div className="quote-card">
                <div className="quote-amount-large">£{defect.quote.amount.toLocaleString()}</div>
                <div className="quote-description-text">{defect.quote.description}</div>
                <div className="quote-meta">Submitted on {defect.quote.submitted}</div>
                {defect.quote.estimatedCompletionDate && (
                  <div className="quote-meta" style={{ marginTop: 4 }}>Estimated completion: {defect.quote.estimatedCompletionDate}</div>
                )}
                <div className="quote-status-banner">
                  <span className="status-icon">⏳</span>
                  Awaiting client approval
                </div>
              </div>
            </div>
          )}

          {/* Complete Job Form */}
          {activeTab === 'approved_jobs' && (
            <form onSubmit={handleCompleteJob}>
              <div className="detail-section">
                <div className="detail-section-title">Approved Quote</div>
                <div className="quote-card">
                  <div className="quote-amount-large">£{defect.quote?.amount.toLocaleString()}</div>
                  <div className="quote-description-text">{defect.quote?.description}</div>
                  <div className="quote-status-banner approved">
                    <span className="status-icon">✓</span>
                    Quote approved - Ready for work
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <div className="detail-section-title">Job Completion</div>
                <div className="form-group">
                  <label className="form-label">Completion Notes *</label>
                  <textarea
                    className="form-textarea"
                    placeholder="Describe the work completed, any issues encountered, follow-up recommendations..."
                    value={completionNotes}
                    onChange={(e) => setCompletionNotes(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Completion Photos</label>
                  <div 
                    className={`image-upload-zone ${dragActive ? 'drag-active' : ''}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => handleFiles(e.target.files)}
                      style={{ display: 'none' }}
                    />
                    <div className="upload-icon">
                      <Icons.Camera />
                    </div>
                    <div className="upload-text">
                      <span className="upload-text-primary">Click to upload</span> or drag and drop
                    </div>
                    <div className="upload-hint">Add photos of completed work</div>
                  </div>
                  
                  {completionImages.length > 0 && (
                    <div className="image-preview-grid">
                      {completionImages.map(image => (
                        <div key={image.id} className="image-preview-item">
                          <img src={image.url} alt={image.name} className="image-preview-img" />
                          <div className="image-preview-overlay">
                            <button 
                              type="button"
                              className="image-remove-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeImage(image.id);
                              }}
                            >
                              <Icons.Close />
                            </button>
                          </div>
                          <div className="image-preview-info">
                            <span className="image-preview-name">{image.name}</span>
                            <span className="image-preview-size">{formatFileSize(image.size)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <button type="submit" className="submit-btn">
                <Icons.Check /> Mark Job Complete
              </button>
            </form>
          )}

          {/* Completed View */}
          {activeTab === 'completed' && (
            <>
              <div className="detail-section">
                <div className="detail-section-title">Quote</div>
                <div className="quote-card">
                  <div className="quote-amount-large">£{defect.quote?.amount.toLocaleString()}</div>
                  <div className="quote-description-text">{defect.quote?.description}</div>
                </div>
              </div>
              {defect.completionNotes && (
                <div className="detail-section">
                  <div className="detail-section-title">Completion Notes</div>
                  <div className="detail-description">{defect.completionNotes}</div>
                </div>
              )}
              {defect.completionImages && defect.completionImages.length > 0 && (
                <div className="detail-section">
                  <div className="detail-section-title">Completion Photos</div>
                  <div className="detail-images-grid">
                    {defect.completionImages.map((image, index) => (
                      <div key={image.id || index} className="detail-image-item">
                        <img src={image.url} alt={image.name || `Image ${index + 1}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Toast Notification
const Toast = ({ message, onClose }) => {
  React.useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="toast">
      <div className="toast-icon">
        <Icons.Check />
      </div>
      <span className="toast-message">{message}</span>
    </div>
  );
};

// ============================================================================
// MAIN APP
// ============================================================================

export default function ContractorPortal() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [contractor, setContractor] = useState(null);
  const [defects, setDefects] = useState([]);
  const [activeTab, setActiveTab] = useState('pending_quote');
  const [selectedDefect, setSelectedDefect] = useState(null);
  const [toast, setToast] = useState(null);

  // Check for magic link token on mount
  React.useEffect(() => {
    const validateToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      // Check for token in URL or localStorage
      const storedToken = localStorage.getItem('contractor_token');
      const authToken = token || storedToken;
      
      if (!authToken) {
        setIsLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`/api/contractor-auth?token=${authToken}`);
        
        if (response.ok) {
          const data = await response.json();
          setContractor(data.contractor);
          setDefects(data.defects || []);
          setIsLoggedIn(true);
          
          // Store token for session persistence
          localStorage.setItem('contractor_token', authToken);
          
          // Clean URL if token was in it
          if (token) {
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } else {
          const error = await response.json();
          setAuthError(error.error || 'Authentication failed');
          localStorage.removeItem('contractor_token');
        }
      } catch (error) {
        console.error('Auth error:', error);
        setAuthError('Failed to authenticate');
        localStorage.removeItem('contractor_token');
      } finally {
        setIsLoading(false);
      }
    };
    
    validateToken();
  }, []);

  const handleLogin = async (email) => {
    setIsLoading(true);
    setAuthError(null);
    
    try {
      const response = await fetch('/api/contractor-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setToast('If an account exists, a login link has been sent to your email.');
      } else {
        setAuthError(data.error || 'Failed to send login link');
      }
    } catch (error) {
      setAuthError('Failed to send login link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    fetch('/api/session', { method: 'DELETE', keepalive: true }).catch(() => {});
    localStorage.removeItem('contractor_token');
    setIsLoggedIn(false);
    setContractor(null);
    setDefects([]);
  };

  const handleSubmitQuote = async (defectId, quote) => {
    try {
      const response = await fetch(`/api/defects/${defectId}/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: quote.amount,
          description: quote.description,
          estimatedCompletionDate: quote.estimatedCompletionDate
        })
      });
      
      if (response.ok) {
        setDefects(defects.map(d => {
          if (d.id === defectId) {
            return { ...d, quote, status: 'quote_received' };
          }
          return d;
        }));
        setSelectedDefect(null);
        setToast('Quote submitted successfully');
      } else {
        const error = await response.json();
        setToast(error.error || 'Failed to submit quote');
      }
    } catch (error) {
      console.error('Quote submission error:', error);
      setToast('Failed to submit quote');
    }
  };

  const handleCompleteJob = async (defectId, completion) => {
    try {
      const response = await fetch(`/api/defects/${defectId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notes: completion.notes,
          images: completion.images
        })
      });
      
      if (response.ok) {
        setDefects(defects.map(d => {
          if (d.id === defectId) {
            return { 
              ...d, 
              status: 'completed',
              completionNotes: completion.notes,
              completionImages: completion.images,
              completedDate: new Date().toISOString().split('T')[0]
            };
          }
          return d;
        }));
        setSelectedDefect(null);
        setToast('Job marked as complete');
      } else {
        const error = await response.json();
        setToast(error.error || 'Failed to complete job');
      }
    } catch (error) {
      console.error('Complete job error:', error);
      setToast('Failed to complete job');
    }
  };

  // Filter defects by tab
  // revision_requested shows in Pending Quote since contractor needs to submit a new quote
  const pendingQuoteDefects = defects.filter(d => 
    (d.status === 'contractor_assigned' && !d.quote) || d.status === 'revision_requested'
  );
  const awaitingApprovalDefects = defects.filter(d => d.status === 'quote_received');
  const approvedJobsDefects = defects.filter(d => d.status === 'quote_approved');
  const completedDefects = defects.filter(d => d.status === 'completed');

  const getTabDefects = () => {
    switch (activeTab) {
      case 'pending_quote': return pendingQuoteDefects;
      case 'awaiting_approval': return awaitingApprovalDefects;
      case 'approved_jobs': return approvedJobsDefects;
      case 'completed': return completedDefects;
      default: return [];
    }
  };

  const getActionLabel = () => {
    switch (activeTab) {
      case 'pending_quote': return 'Submit Quote';
      case 'approved_jobs': return 'Complete Job';
      default: return 'View Details';
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <>
        <style>{styles}</style>
        <div className="login-container">
          <div className="login-card" style={{ textAlign: 'center' }}>
            <div className="login-logo" style={{ justifyContent: 'center' }}>
              <div className="login-logo-icon">
                <Icons.Wrench />
              </div>
            </div>
            <p style={{ color: 'var(--text-secondary)', marginTop: 16 }}>Loading...</p>
          </div>
        </div>
      </>
    );
  }

  if (!isLoggedIn) {
    return (
      <>
        <style>{styles}</style>
        <ContractorLogin onLogin={handleLogin} error={authError} />
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="portal-layout">
        <header className="portal-header">
          <div className="portal-logo">
            <div className="portal-logo-icon">
              <Icons.Wrench />
            </div>
            <div>
              <div className="portal-logo-text">Contractor Portal</div>
              <div className="portal-logo-subtitle">DefectTrack</div>
            </div>
          </div>
          <div className="portal-user">
            <div className="portal-user-info">
              <div className="portal-user-name">{contractor?.name}</div>
              <div className="portal-user-email">{contractor?.email}</div>
            </div>
            <button className="logout-btn" onClick={handleLogout}>
              Sign Out
            </button>
          </div>
        </header>

        <main className="portal-content">
          <div className="page-header">
            <h1 className="page-title">Your Assigned Defects</h1>
            <p className="page-subtitle">View defects, submit quotes, and complete jobs</p>
          </div>

          <div className="portal-tabs">
            <button 
              className={`portal-tab ${activeTab === 'pending_quote' ? 'active' : ''}`}
              onClick={() => setActiveTab('pending_quote')}
            >
              Pending Quote
              {pendingQuoteDefects.length > 0 && (
                <span className="tab-badge">{pendingQuoteDefects.length}</span>
              )}
            </button>
            <button 
              className={`portal-tab ${activeTab === 'awaiting_approval' ? 'active' : ''}`}
              onClick={() => setActiveTab('awaiting_approval')}
            >
              Awaiting Approval
              {awaitingApprovalDefects.length > 0 && (
                <span className="tab-badge yellow">{awaitingApprovalDefects.length}</span>
              )}
            </button>
            <button 
              className={`portal-tab ${activeTab === 'approved_jobs' ? 'active' : ''}`}
              onClick={() => setActiveTab('approved_jobs')}
            >
              Approved Jobs
              {approvedJobsDefects.length > 0 && (
                <span className="tab-badge green">{approvedJobsDefects.length}</span>
              )}
            </button>
            <button 
              className={`portal-tab ${activeTab === 'completed' ? 'active' : ''}`}
              onClick={() => setActiveTab('completed')}
            >
              Completed
            </button>
          </div>

          {getTabDefects().length > 0 ? (
            <div className="defects-grid">
              {getTabDefects().map(defect => (
                <div 
                  key={defect.id} 
                  className="defect-card"
                  onClick={() => setSelectedDefect(defect)}
                >
                  <div className="defect-card-header">
                    <span className="defect-id">{defect.id}</span>
                    <span className={`severity-badge ${defect.severity}`}>
                      <span className="severity-dot"></span>
                      {defect.severity.charAt(0).toUpperCase() + defect.severity.slice(1)}
                    </span>
                  </div>
                  <h3 className="defect-card-title">{defect.title}</h3>
                  <p className="defect-card-store">Store: {defect.location}</p>
                  <p className="defect-card-description">{defect.description}</p>
                  {defect.quote && (
                    <div className="defect-card-quote">
                      <span className="quote-label">Your Quote:</span>
                      <span className="quote-value">£{defect.quote.amount?.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="defect-card-footer">
                    <span className="defect-card-date">Reported: {defect.reportedDate}</span>
                    <button className="action-btn">
                      {getActionLabel()}
                      <Icons.Arrow />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">
                {activeTab === 'pending_quote' ? '📋' : 
                 activeTab === 'awaiting_approval' ? '⏳' :
                 activeTab === 'approved_jobs' ? '🔧' : '✓'}
              </div>
              <div className="empty-state-title">
                {activeTab === 'pending_quote' ? 'No defects awaiting quote' : 
                 activeTab === 'awaiting_approval' ? 'No quotes awaiting approval' :
                 activeTab === 'approved_jobs' ? 'No approved jobs to complete' : 'No completed jobs yet'}
              </div>
              <div className="empty-state-text">
                {activeTab === 'pending_quote' ? 'New assignments will appear here' : 
                 activeTab === 'awaiting_approval' ? 'Submitted quotes will appear here' :
                 activeTab === 'approved_jobs' ? 'Approved quotes will appear here for completion' : 'Completed jobs will be shown here'}
              </div>
            </div>
          )}
        </main>
      </div>

      {selectedDefect && (
        <DefectModal
          defect={selectedDefect}
          activeTab={activeTab}
          onClose={() => setSelectedDefect(null)}
          onSubmitQuote={handleSubmitQuote}
          onCompleteJob={handleCompleteJob}
        />
      )}

      {toast && (
        <Toast 
          message={toast} 
          onClose={() => setToast(null)} 
        />
      )}
    </>
  );
}
